import { createBrowserRouter, type RouteObject } from "react-router";

import { Navigate } from "react-router";

import { AppsPage } from "@/features/activity/AppsPage";
import { NowPage } from "@/features/activity/NowPage";
import { TimelinePage } from "@/features/activity/TimelinePage";
import { DevicePage } from "@/features/auth/DevicePage";
import { ForgotPasswordPage } from "@/features/auth/ForgotPasswordPage";
import { InvitePage } from "@/features/auth/InvitePage";
import { LoginPage } from "@/features/auth/LoginPage";
import { RequireAuth, RequirePermission } from "@/features/auth/RequireAuth";
import { ResetPasswordPage } from "@/features/auth/ResetPasswordPage";
import { SetupPage } from "@/features/auth/SetupPage";
import { DashboardPage } from "@/features/dashboard/DashboardPage";
import { DownloadPage } from "@/features/download/DownloadPage";
import { PeoplePage } from "@/features/people/PeoplePage";
import { MyTasksPage } from "@/features/projects/MyTasksPage";
import { ProjectPage } from "@/features/projects/ProjectPage";
import { ProjectsPage } from "@/features/projects/ProjectsPage";
import { ReportsPage } from "@/features/reports/ReportsPage";
import { AuditPage } from "@/features/settings/AuditPage";
import { CategoriesPage } from "@/features/settings/CategoriesPage";
import { CompanyPage } from "@/features/settings/CompanyPage";
import { MonitoringPage } from "@/features/settings/MonitoringPage";
import { ProfilePage } from "@/features/settings/ProfilePage";
import { ReleasesPage } from "@/features/settings/ReleasesPage";
import { SecurityPage } from "@/features/settings/SecurityPage";
import { ServerPage } from "@/features/settings/ServerPage";
import { SettingsLayout } from "@/features/settings/SettingsLayout";
import { ComingSoonPage } from "@/features/system/ComingSoonPage";
import { NotFoundPage } from "@/features/system/NotFoundPage";
import { RouteErrorPage } from "@/features/system/RouteErrorPage";
import { TeamsPage } from "@/features/teams/TeamsPage";
import { ManualTimePage } from "@/features/timesheets/ManualTimePage";
import { TeamTimesheetPage } from "@/features/timesheets/TeamTimesheetPage";
import { TimesheetPage } from "@/features/timesheets/TimesheetPage";
import { VacationsPage } from "@/features/vacations/VacationsPage";

import { AppLayout } from "./layout/AppLayout";
import { navItems } from "./navigation";

const comingSoonRoutes: RouteObject[] = navItems.flatMap((item) =>
  item.stage === undefined
    ? []
    : [{ path: item.path, element: <ComingSoonPage section={item.key} stage={item.stage} /> }],
);

export const routes: RouteObject[] = [
  { path: "/login", element: <LoginPage />, errorElement: <RouteErrorPage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage />, errorElement: <RouteErrorPage /> },
  { path: "/reset-password", element: <ResetPasswordPage />, errorElement: <RouteErrorPage /> },
  { path: "/invite", element: <InvitePage />, errorElement: <RouteErrorPage /> },
  { path: "/setup", element: <SetupPage />, errorElement: <RouteErrorPage /> },
  {
    path: "/device",
    element: (
      <RequireAuth>
        <DevicePage />
      </RequireAuth>
    ),
    errorElement: <RouteErrorPage />,
  },
  {
    path: "/",
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: "/activity", element: <TimelinePage /> },
      { path: "/activity/apps", element: <AppsPage /> },
      {
        path: "/activity/now",
        element: (
          <RequirePermission permission="view_team">
            <NowPage />
          </RequirePermission>
        ),
      },
      { path: "/activity/:userId", element: <TimelinePage /> },
      { path: "/download", element: <DownloadPage /> },
      { path: "/timesheets", element: <TimesheetPage /> },
      { path: "/timesheets/manual", element: <ManualTimePage /> },
      {
        path: "/timesheets/team",
        element: (
          <RequirePermission permission="view_team">
            <TeamTimesheetPage />
          </RequirePermission>
        ),
      },
      { path: "/time-off", element: <VacationsPage /> },
      { path: "/projects", element: <ProjectsPage /> },
      { path: "/projects/:projectId", element: <ProjectPage /> },
      { path: "/tasks", element: <MyTasksPage /> },
      { path: "/reports", element: <ReportsPage /> },
      { path: "/reports/:tab", element: <ReportsPage /> },
      {
        path: "/people",
        element: (
          <RequirePermission permission="view_team">
            <PeoplePage />
          </RequirePermission>
        ),
      },
      {
        path: "/teams",
        element: (
          <RequirePermission permission="view_team">
            <TeamsPage />
          </RequirePermission>
        ),
      },
      {
        path: "/settings",
        element: <SettingsLayout />,
        children: [
          { index: true, element: <Navigate to="profile" replace /> },
          { path: "profile", element: <ProfilePage /> },
          { path: "security", element: <SecurityPage /> },
          {
            path: "company",
            element: (
              <RequirePermission permission="manage_company">
                <CompanyPage />
              </RequirePermission>
            ),
          },
          {
            path: "monitoring",
            element: (
              <RequirePermission permission="view_team">
                <MonitoringPage />
              </RequirePermission>
            ),
          },
          {
            path: "categories",
            element: (
              <RequirePermission permission="view_team">
                <CategoriesPage />
              </RequirePermission>
            ),
          },
          {
            path: "server",
            element: (
              <RequirePermission permission="manage_company">
                <ServerPage />
              </RequirePermission>
            ),
          },
          {
            path: "releases",
            element: (
              <RequirePermission permission="manage_company">
                <ReleasesPage />
              </RequirePermission>
            ),
          },
          {
            path: "audit",
            element: (
              <RequirePermission permission="view_audit_log">
                <AuditPage />
              </RequirePermission>
            ),
          },
        ],
      },
      ...comingSoonRoutes,
      ...(import.meta.env.DEV
        ? [
            {
              path: "/design-system",
              lazy: async () => {
                const m = await import("@/features/design-system/DesignSystemPage");
                return { Component: m.DesignSystemPage };
              },
            },
          ]
        : []),
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];

export function createRouter() {
  return createBrowserRouter(routes);
}
